import { useEffect } from "react";

/**
 * Comportamiento móvil compartido por los dos sheets (panel del negocio y superadmin):
 * bloqueo de scroll, sheet pegado al área visible con el teclado abierto y el campo
 * enfocado siempre a la vista. Mismas técnicas que react-aria (usePreventScroll) y Vaul:
 *
 * - El contenedor (scrim) cubre SIEMPRE toda la pantalla y solo el panel se mueve para
 *   quedar en el fondo del área visible (variables `--kb`/`--vvh` en el contenedor; el
 *   CSS extiende el fondo del panel hacia abajo para que no quede franja entre panel y
 *   teclado, por la que se veía la página de atrás y la barra flotante de Safari).
 * - Lo que aprendimos con grabaciones de un iPhone con iOS 26 (2026-10-06):
 *   `window.innerHeight` se encoge con el teclado (no sirve para medirlo; se usa el alto
 *   del contenedor fijo); al enfocar, iOS desplaza la vista ~135px y la regresa despacio
 *   aunque se intente impedir; pelear con eso (anclar la ventana, corregir cuando "se
 *   calma") era lo que se veía como brincos. Ahora el desplazamiento se sigue cuadro a
 *   cuadro sin animar y solo el cambio de alto del teclado se anima.
 * - Al enfocar, el campo se saca de pantalla un frame para que Safari desplace lo menos
 *   posible; luego lo mostramos nosotros, desplazando solo el cuerpo del sheet.
 */

export const isIOS =
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.platform) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

// Duración y curva parecidas a la animación del teclado del sistema, para moverse a la par.
const KB_TRANSITION_MS = 250;
const KB_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";
const KB_MIN_HEIGHT = 120; // px: menos que esto no es un teclado (barra del navegador, etc.)
const PREDICT_TIMEOUT_MS = 700;
// Cuánto esperar sin nuevos `resize` para dar por terminado el movimiento del teclado.
const SETTLE_MS = 60;
const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
// `translate` como propiedad propia (no `transform`): se suma al transform con el que el
// sheet entra y se arrastra, sin pisarlo. iOS 14.5+ / Chrome 104+; si no, sin animación.
const canAnimateTranslate = () =>
  typeof Element !== "undefined" && typeof Element.prototype.animate === "function" &&
  typeof CSS !== "undefined" && CSS.supports?.("translate", "0 1px");

// Alto del teclado aprendido, por ancho de pantalla (orientación) y tipo de teclado (el
// numérico del teléfono es más bajo que el de texto). En localStorage para que sirva
// desde el primer toque tras recargar; si no hay almacenamiento, solo dura la sesión.
const kbMemory = new Map();
// "kb3": los valores guardados por versiones anteriores se medían distinto; se descartan.
const kbKey = (el) => `sheet-kb3:${window.innerWidth}:${el?.inputMode || el?.type || "text"}`;
function rememberKeyboard(el, height) {
  const key = kbKey(el);
  const value = Math.round(height);
  kbMemory.set(key, value);
  try { localStorage.setItem(key, String(value)); } catch { /* modo privado: solo memoria */ }
}
function recallKeyboard(el) {
  const key = kbKey(el);
  if (kbMemory.has(key)) return kbMemory.get(key);
  try { return Number(localStorage.getItem(key)) || 0; } catch { return 0; }
}

const NON_TEXT_INPUTS = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "hidden", "image"]);
const isTextField = (el) =>
  !!el && ((el.tagName === "INPUT" && !NON_TEXT_INPUTS.has(el.type)) || el.tagName === "TEXTAREA" || el.isContentEditable);

/**
 * Desplaza solo el cuerpo del sheet (nunca la página) para dejar el campo CENTRADO en lo
 * que se ve del sheet, no solo "a la vista": con el teclado abierto queda poco espacio y
 * centrado siempre deja ver la etiqueta de arriba y el campo siguiente. Si el campo está
 * muy arriba o muy abajo del formulario, llega hasta donde se pueda.
 */
function revealInSheet(el, scroller) {
  if (!el || !scroller || !scroller.contains(el)) return;
  const s = scroller.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const delta = (r.top + r.height / 2) - (s.top + s.height / 2);
  const max = scroller.scrollHeight - scroller.clientHeight;
  const top = Math.min(max, Math.max(0, scroller.scrollTop + delta));
  if (Math.abs(top - scroller.scrollTop) < 2) return;
  // Suave: de golpe se veía como un segundo brinco justo después de que el sheet subió.
  scroller.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
}

// Contador global: con sheets anidados (p. ej. lista de números → "Agregar otro") el
// primero bloquea y el último en cerrarse restaura, no cada uno por su cuenta.
let lockCount = 0;
let restoreLock = null;

function lockScroll() {
  if (lockCount++ > 0) return;
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  const html = document.documentElement;
  const body = document.body;
  const prev = {
    htmlOverflow: html.style.overflow,
    overflow: body.style.overflow,
    position: body.style.position,
    top: body.style.top,
    width: body.style.width,
  };
  // `overflow: hidden` solo no basta en iOS Safari: el body se fija en su scroll actual.
  // usePullToRefresh lee `body.style.overflow === "hidden"` para no dispararse encima.
  html.style.overflow = "hidden";
  body.style.overflow = "hidden";
  body.style.position = "fixed";
  body.style.top = `-${scrollY}px`;
  body.style.width = "100%";
  // Sin "anclar" la ventana con scrollTo(0, 0) en cada scroll: en el iPhone peleaba con el
  // desplazamiento propio de iOS al abrir el teclado y lo hacía regresar lento, ~2.5 s
  // (grabación del 2026-10-06). El sheet ya sigue a la vista visible (abajo).
  restoreLock = () => {
    html.style.overflow = prev.htmlOverflow;
    body.style.overflow = prev.overflow;
    body.style.position = prev.position;
    body.style.top = prev.top;
    body.style.width = prev.width;
    window.scrollTo(scrollX, scrollY);
  };
}

function unlockScroll() {
  if (--lockCount > 0) return;
  lockCount = 0;
  restoreLock?.();
  restoreLock = null;
}

/**
 * @param mounted   el sheet está en el DOM
 * @param rootRef   contenedor fijo (scrim + panel)
 * @param panelRef  el panel
 * @param bodySelector  selector del cuerpo con scroll dentro del panel
 */
export default function useSheetViewport({ mounted, rootRef, panelRef, bodySelector }) {
  useEffect(() => {
    if (!mounted) return undefined;
    lockScroll();
    return unlockScroll;
  }, [mounted]);

  // El panel queda SIEMPRE pegado al fondo del área visible. Dos tipos de cambio:
  //  - iOS desplaza la vista (al enfocar sube ~135px de golpe y luego la regresa despacio,
  //    visto en el iPhone): se sigue en el mismo cuadro, SIN animar, para que el panel no se
  //    mueva en pantalla. Esperar a que se calmara era lo que acababa en brinco.
  //  - El teclado cambia el alto visible: UN solo deslizamiento por GPU (`translate`). El
  //    navegador manda varios `resize` mientras el teclado se anima; se espera a que se
  //    calme para no hacer escalerita, y con el alto aprendido se arranca a la par.
  // Variables en el contenedor (las usa el CSS): `--kb` = cuánto subir el panel (puede ser
  // negativo mientras iOS desplaza la vista) y `--vvh` = alto visible, para el alto máximo.
  useEffect(() => {
    if (!mounted) return undefined;
    const root = rootRef.current;
    const panel = panelRef.current;
    const vv = window.visualViewport;
    if (!root || !panel || !vv) return undefined;
    const animated = !prefersReducedMotion() && canAnimateTranslate();
    // Alto del contenedor fijo = viewport de layout, que en iOS NO se encoge con el teclado.
    // No usar `window.innerHeight`: en iOS Safari sí se encoge, y la diferencia daba ~0.
    const layoutHeight = () => root.clientHeight;
    // ¿Hay teclado? (la barra flotante de Safari también resta, pero es mucho menos).
    const keyboardOpen = (visible = vv.height) => layoutHeight() - visible > KB_MIN_HEIGHT;
    let applied = { visible: vv.height, offset: vv.offsetTop };
    let closedVisible = keyboardOpen() ? layoutHeight() : vv.height; // alto visible sin teclado
    let anim = null;
    let settleTimer = 0;
    let predictTimer = 0;
    let predicting = false;
    let revealTimer = 0;
    let raf = 0;

    const write = ({ visible, offset }) => {
      root.style.setProperty("--vvh", `${visible}px`);
      root.style.setProperty("--kb", `${layoutHeight() - offset - visible}px`);
    };

    // Un solo deslizamiento hacia un alto visible nuevo. Se aplica el valor final de una vez
    // y se anima `translate` desde donde se veía el panel (si venía a medias, desde ahí: sin
    // salto). Al subir, el fondo extendido del panel (CSS) tapa lo que queda debajo.
    const glideTo = (visible) => {
      const next = { visible: Math.round(visible), offset: vv.offsetTop };
      if (Math.abs(next.visible - applied.visible) < 2) {
        applied = next;
        write(applied);
        return;
      }
      const t0 = panel.getBoundingClientRect().top;
      anim?.cancel();
      anim = null;
      applied = next;
      write(applied);
      if (!animated) return;
      const dy = t0 - panel.getBoundingClientRect().top;
      if (Math.abs(dy) < 1) return;
      anim = panel.animate([{ translate: `0 ${dy}px` }, { translate: "0 0" }], { duration: KB_TRANSITION_MS, easing: KB_EASING });
      anim.onfinish = () => { anim = null; };
    };

    // Desplazamiento de iOS: mismo alto, otra posición. Se compensa al momento; si hay un
    // deslizamiento en curso, sigue (es relativo) y no se interrumpe.
    const follow = () => {
      raf = 0;
      if (Math.abs(vv.offsetTop - applied.offset) < 1) return;
      applied = { ...applied, offset: vv.offsetTop };
      write(applied);
    };

    const reveal = () => {
      const active = document.activeElement;
      if (isTextField(active)) revealInSheet(active, panel.querySelector(bodySelector));
    };
    // El alto visible dejó de cambiar: el valor final, en un solo movimiento.
    const settle = () => {
      clearTimeout(predictTimer);
      predicting = false;
      const visible = vv.height;
      if (keyboardOpen(visible)) rememberKeyboard(document.activeElement, layoutHeight() - visible);
      else closedVisible = visible;
      glideTo(visible);
      // El input se centra YA, sin esperar a que el panel termine de deslizarse: el
      // deslizamiento es un `translate` que mueve igual al campo y al cuerpo del sheet, y
      // el alto ya es el final, así que la posición relativa medida ahora es la buena. (Antes
      // se esperaban 280 ms y el campo se centraba casi un segundo después del toque.)
      clearTimeout(revealTimer);
      revealTimer = setTimeout(reveal, 0);
    };
    const scheduleSettle = () => { clearTimeout(settleTimer); settleTimer = setTimeout(settle, SETTLE_MS); };
    const onResize = () => {
      if (!raf) raf = requestAnimationFrame(follow);
      if (Math.abs(vv.height - applied.visible) >= 2) scheduleSettle();
    };
    // Un `scroll` mientras se anticipa el teclado NO dispara el ajuste: el teclado aún no
    // avisa su alto y el ajuste regresaría el panel al alto viejo antes de que llegue.
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(follow);
      if (!predicting && Math.abs(vv.height - applied.visible) >= 2) scheduleSettle();
    };

    // Anticipar el teclado: el navegador avisa hasta que el teclado ya se movió, así que
    // esperar al aviso hacía que el sheet subiera después. Con el alto aprendido, el sheet
    // arranca en el mismo instante que el teclado y, si el aviso real coincide, ya no se
    // vuelve a mover. Si el teclado no aparece (teclado físico), el tiempo límite lo regresa.
    const anticipate = (visible) => {
      predicting = true;
      glideTo(visible);
      clearTimeout(predictTimer);
      predictTimer = setTimeout(settle, PREDICT_TIMEOUT_MS);
    };
    const onFocusIn = (e) => {
      if (!isTextField(e.target)) return;
      if (keyboardOpen()) {
        // Pasar de un campo a otro con el teclado abierto: no habrá `resize` que lo
        // acomode, así que se centra aquí. Dos frames: en iOS el campo está fuera de
        // pantalla un frame (truco de abajo) y medirlo antes daría una posición falsa.
        clearTimeout(revealTimer);
        requestAnimationFrame(() => requestAnimationFrame(reveal));
        return;
      }
      const kb = recallKeyboard(e.target);
      if (kb > KB_MIN_HEIGHT) {
        anticipate(layoutHeight() - kb);
        // Con el alto del teclado ya conocido se centra a la par del deslizamiento, sin
        // esperar al aviso del navegador. 40 ms ≈ dos cuadros: en iOS el campo está fuera de
        // pantalla un cuadro (truco de abajo) y medirlo antes daría una posición falsa.
        clearTimeout(revealTimer);
        revealTimer = setTimeout(reveal, 40);
      }
    };
    const onFocusOut = (e) => {
      if (!isTextField(e.target) || !keyboardOpen()) return;
      // Pasar de un campo a otro deja el teclado abierto: nada que anticipar.
      if (isTextField(e.relatedTarget) && panel.contains(e.relatedTarget)) return;
      anticipate(closedVisible);
    };

    write(applied);
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onScroll);
    panel.addEventListener("focusin", onFocusIn);
    panel.addEventListener("focusout", onFocusOut);
    return () => {
      anim?.cancel();
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(settleTimer);
      clearTimeout(predictTimer);
      clearTimeout(revealTimer);
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onScroll);
      panel.removeEventListener("focusin", onFocusIn);
      panel.removeEventListener("focusout", onFocusOut);
      root.style.removeProperty("--kb");
      root.style.removeProperty("--vvh");
    };
  }, [mounted, rootRef, panelRef, bodySelector]);

  // iOS: que Safari no desplace la página al enfocar un campo (el "fondo que se mueve").
  useEffect(() => {
    if (!mounted || !isIOS) return undefined;
    const panel = panelRef.current;
    if (!panel) return undefined;
    const onFocusIn = (e) => {
      const target = e.target;
      if (!isTextField(target)) return;
      const prevTransform = target.style.transform;
      target.style.transform = "translateY(-2000px)";
      // El centrado del campo lo hace el efecto de arriba (al cambiar de campo o cuando el
      // teclado termina de acomodarse).
      requestAnimationFrame(() => { target.style.transform = prevTransform; });
    };
    panel.addEventListener("focusin", onFocusIn);
    return () => panel.removeEventListener("focusin", onFocusIn);
  }, [mounted, panelRef]);
}
